import { computed, peek } from "../../core/signals/primitives.js";
import { defineBehavior } from "../../core/composition/define-behavior.js";
import { effect } from "../../core/signals/effect.js";
import { createMachineReactor } from "../../core/reactors/create-machine-reactor.js";
import { isWebKitAirPlayCapable, listen } from "@videojs/utils/dom";
//#region src/playback/behaviors/dom/airplay.ts
/**
* **Bridge MSE playback to AirPlay on WebKit.** MSE streams can't be handed to an AirPlay receiver directly. The
* WebKit-recommended workaround is to append a fallback `<source type="application/x-mpegURL">` carrying the original
* manifest URL: Safari exposes the AirPlay picker and, when a wireless target is selected, plays that native-HLS source
* on the receiver. The session state (WebKit's wireless flag, falling edge debounced — see `REMOTE_INACTIVE_SETTLE_MS`)
* is written straight to its policy consequences, declared here so the cause→policy mapping stays with the feature:
*
* - `state.loadingSuspended` — held while the session is live. Observed by the `loadXSegments` dispatchers (no fetching
*   alongside the receiver) and by `setupMediaSource` (its post-close rebuild waits — attaching runs `element.load()`
*   under the live receiver, which destroys a session still being established). The suspension this behavior holds
*   doubles as its own session fact — same writer, same edges.
* - `state.startPosition` — one-shot command: the position is captured from the element at the session's settled end
*   (still receiver-mirrored) and written once the rebuild's `load()` resets the element (its `'emptied'`), so
*   `applyStartPosition` applies it to the rebuilt source — never to the pre-rebuild element — and starts it where the
*   receiver left off. The playing state rides the same snapshot but stays behavior-local: this behavior itself calls
*   `play()` once the command has been _consumed_ — i.e. after the seek — when the receiver was playing at session end.
*   The whole restore is bound to the presentation the session owned and retracted if that changes.
*
* A source change during a live session releases the hold rather than deferring until the session ends, so the rebuild
* runs and WebKit switches the receiver to the newly-built AirPlay alternate. Measured, not contracted — see the effect
* below. https://webkit.org/blog/15036/how-to-use-media-source-extensions-with-airplay/
*
* Single-positive-state reactor (`'preconditions-unmet'` ↔ `'airplay-capable'`): gated on a WebKit-AirPlay-capable
* media element being in scope. The entry — gated on `context.mediaSource` — appends the fallback `<source>` (kept
* current from `state.presentation`) and enables the AirPlay picker once the MediaSource is open, removing the source
* the moment the MediaSource detaches so it never survives an MSE teardown. State-exit cleanup (author opt-out, detach,
* source reset, behavior destroy) removes the source and restores the element's `disableRemotePlayback` default. No-op
* on non-WebKit platforms (Chromium, Firefox) — `deriveState` never leaves `'preconditions-unmet'`.
*
* MMS and AirPlay want _opposite_ values of `disableRemotePlayback` on the same element, so it is **sequenced**:
*
* - **MMS needs `true` to open.** `setupMediaSource` sets `disableRemotePlayback = true` when it attaches a
*   ManagedMediaSource — Safari won't fire `sourceopen` (and MSE playback never starts) otherwise.
* - **AirPlay needs `false` to offer the picker.** Flipping to `false` _before_ the source opens would prevent
*   `sourceopen`, so the flip is gated on `context.mediaSource` — which `setupMediaSource` publishes exactly once the
*   MS is open. Re-fires per source (the slot clears + republishes on reset).
* - **Author opt-out wins.** `state.disableRemotePlayback` is the author's intent, written only by the media adapter's
*   IDL property; MMS/programmatic code touch the element's own `disableRemotePlayback` instead. A `true` there is
*   unambiguously the author's choice to disable remote playback, so it holds the machine in `'preconditions-unmet'`
*   and nothing is set up.
*/
/**
* How long WebKit's wireless flag must read _inactive_ before the session-driven `loadingSuspended` clears.
*
* Measured on Safari 26.4 (macOS): when an AirPlay session engages, Safari closes the ManagedMediaSource and — while
* its pipeline switches to the native-HLS fallback source — transiently reports `webkitCurrentPlaybackTargetIsWireless
* === false`, firing the changed event. Trusting an instantaneous inactive reading would release `setupMediaSource`'s
* rebuild hold mid-handoff; its recovery `load()` then destroys the very session being established. Rising edges apply
* immediately; only the falling edge waits out this settle window.
*/
const REMOTE_INACTIVE_SETTLE_MS = 1e3;
function deriveState(mediaElement, authorDisabledRemotePlayback) {
	if (!mediaElement || !isWebKitAirPlayCapable(mediaElement)) return "preconditions-unmet";
	if (authorDisabledRemotePlayback) return "preconditions-unmet";
	return "airplay-capable";
}
function setupAirPlaySetup({ state, context }) {
	const derivedStateSignal = computed(() => deriveState(context.mediaElement.get(), state.disableRemotePlayback.get()));
	return createMachineReactor({
		initial: "preconditions-unmet",
		monitor: () => derivedStateSignal.get(),
		states: {
			"preconditions-unmet": {},
			"airplay-capable": { entry: () => {
				const mediaElement = context.mediaElement.get();
				const isSessionActive = () => !!mediaElement.webkitCurrentPlaybackTargetIsWireless;
				let settleTimer;
				let sessionPresentationUrl;
				let pendingRestore;
				/** Presentation an already-written `state.startPosition` belongs to. */
				let restoreOwnerUrl;
				let resumeWhenRestored = false;
				const sync = () => {
					if (isSessionActive()) {
						clearTimeout(settleTimer);
						settleTimer = void 0;
						if (!peek(state.loadingSuspended)) sessionPresentationUrl = peek(state.presentation)?.url;
						state.loadingSuspended.set(true);
					} else if (peek(state.loadingSuspended)) settleTimer ??= setTimeout(() => {
						settleTimer = void 0;
						const stillActive = isSessionActive();
						if (!stillActive) {
							const ownerUrl = sessionPresentationUrl;
							sessionPresentationUrl = void 0;
							if (peek(state.presentation)?.url === ownerUrl) pendingRestore = {
								position: mediaElement.currentTime,
								wasPlaying: !mediaElement.paused,
								presentationUrl: ownerUrl
							};
						}
						state.loadingSuspended.set(stillActive);
					}, REMOTE_INACTIVE_SETTLE_MS);
					else state.loadingSuspended.set(false);
				};
				const listenerCleanup = new AbortController();
				listen(mediaElement, "webkitcurrentplaybacktargetiswirelesschanged", sync, { signal: listenerCleanup.signal });
				listen(mediaElement, "emptied", () => {
					if (!pendingRestore) return;
					const { position, wasPlaying, presentationUrl } = pendingRestore;
					pendingRestore = void 0;
					if (peek(state.presentation)?.url !== presentationUrl) return;
					state.startPosition.set(position);
					restoreOwnerUrl = presentationUrl;
					resumeWhenRestored = wasPlaying;
				}, { signal: listenerCleanup.signal });
				const disposeSourceChangeEnd = effect(() => {
					const url = state.presentation.get()?.url;
					if (!peek(state.loadingSuspended) || url === sessionPresentationUrl) return;
					sessionPresentationUrl = void 0;
					clearTimeout(settleTimer);
					settleTimer = void 0;
					state.loadingSuspended.set(false);
				});
				const disposeRestoreWatch = effect(() => {
					const url = state.presentation.get()?.url;
					const position = state.startPosition.get();
					if (!restoreOwnerUrl) return;
					if (url !== restoreOwnerUrl) {
						restoreOwnerUrl = void 0;
						resumeWhenRestored = false;
						if (position !== void 0) state.startPosition.set(void 0);
						return;
					}
					if (position !== void 0) return;
					restoreOwnerUrl = void 0;
					if (!resumeWhenRestored) return;
					resumeWhenRestored = false;
					mediaElement.play().catch((err) => {
						console.warn("[setupAirPlay] session-end resume play() rejected — staying paused:", err);
					});
				});
				let sourceEl = null;
				const disposeSource = effect(() => {
					const hasMediaSource = !!context.mediaSource.get();
					const sessionActive = !!state.loadingSuspended.get();
					const url = state.presentation.get()?.url ?? "";
					if (!hasMediaSource && !sessionActive) {
						sourceEl?.remove();
						sourceEl = null;
					} else if (hasMediaSource && (!sourceEl || sourceEl.parentNode !== mediaElement)) {
						sourceEl = document.createElement("source");
						sourceEl.type = "application/x-mpegURL";
						mediaElement.append(sourceEl);
						mediaElement.disableRemotePlayback = false;
					}
					if (sourceEl) sourceEl.src = url;
				});
				sync();
				return () => {
					disposeSource();
					disposeSourceChangeEnd();
					disposeRestoreWatch();
					listenerCleanup.abort();
					clearTimeout(settleTimer);
					sourceEl?.remove();
					sourceEl = null;
					mediaElement.disableRemotePlayback = true;
					state.loadingSuspended.set(false);
					if (restoreOwnerUrl) {
						restoreOwnerUrl = void 0;
						resumeWhenRestored = false;
						if (peek(state.startPosition) !== void 0) state.startPosition.set(void 0);
					}
				};
			} }
		}
	});
}
const setupAirPlay = defineBehavior({
	stateKeys: [
		"presentation",
		"disableRemotePlayback",
		"loadingSuspended",
		"startPosition"
	],
	contextKeys: ["mediaElement", "mediaSource"],
	setup: setupAirPlaySetup
});
//#endregion
export { setupAirPlay };
